import { ApiError, getSession, loadEnv } from '@/core';
import { httpClient } from './httpClient';
import { parseServiceOrder } from './schemas/service';
import type { ServiceOrder } from '@/domain/service';

function technicianId(): string {
  const user = getSession().user;
  if (user === null) throw new Error('Sessão sem técnico vinculado.');
  return user.id;
}

function basePath(requestId: string): string {
  return `/technicians/${technicianId()}/maintenance-requests/${requestId}/service-order`;
}

/** POST/GET /technicians/{id}/maintenance-requests/{id}/service-order — abre/devolve o rascunho. */
export async function getOrCreateServiceOrder(requestId: string): Promise<ServiceOrder> {
  const payload = await httpClient.get<unknown>(basePath(requestId));
  return parseServiceOrder(payload);
}

export interface ServiceOrderDraft {
  diagnosis: string | null;
  servicePerformed: string | null;
  timeSpentMinutes: number | null;
  parts: Array<{ description: string; quantity: number; unitAmount: number; isNoCharge?: boolean; noChargeReason?: string }> | null;
  // B14 — recomendações para a clínica e próxima manutenção ({1 mês, 3 meses, 6 meses, 12 meses},
  // validado no backend). Opcionais no tipo porque a action legada do store monta o rascunho sem
  // estes campos; ausentes, o backend os trata como null (regra #17b).
  recommendations?: string | null;
  nextMaintenance?: string | null;
}

/** PUT …/service-order — salva o rascunho sem exigir os 7 campos. */
export async function saveServiceOrderDraft(requestId: string, draft: ServiceOrderDraft): Promise<ServiceOrder> {
  const payload = await httpClient.put<unknown>(basePath(requestId), draft);
  return parseServiceOrder(payload);
}

export interface CloseServiceOrderInput {
  draft?: ServiceOrderDraft;
  signerName: string;
  signerDataUrl: string;
  signerLatitude: number | null;
  signerLongitude: number | null;
  signerLocationAccuracyMeters: number | null;
  signerLocationAbsenceReason: string | null;
  acceptedClinicAmount: number | null;
  // B14 — opcionais porque a fila offline (offlineQueue.ts) revalida o payload cifrado com um
  // schema compatível com estes campos; ausentes, o backend preserva o rascunho (regra #17b).
  recommendations?: string | null;
  nextMaintenance?: string | null;
}

/**
 * POST …/service-order/close — fechamento multipart: a assinatura congela o que está na tela.
 * Multipart usa o httpClient compartilhado: CSRF em memória e replay único após refresh401.
 */
export async function closeServiceOrder(requestId: string, input: CloseServiceOrderInput, operationId?: string): Promise<ServiceOrder> {
  const form = new FormData();
  if (input.draft !== undefined) form.append('draft', JSON.stringify(input.draft));
  form.append('signerName', input.signerName);
  if (operationId !== undefined) form.append('operationId', operationId);
  // Contrato multipart real (TechServiceOrderEndpoints.cs): o arquivo viaja em form.Files["signature"].
  form.append('signature', dataUrlToBlob(input.signerDataUrl), 'assinatura.png');
  if (input.signerLatitude !== null) form.append('signerLatitude', String(input.signerLatitude));
  if (input.signerLongitude !== null) form.append('signerLongitude', String(input.signerLongitude));
  // O endpoint lê form["signerLocationAccuracyM"] (sem o sufixo "eters").
  if (input.signerLocationAccuracyMeters !== null) form.append('signerLocationAccuracyM', String(input.signerLocationAccuracyMeters));
  if (input.signerLocationAbsenceReason !== null) form.append('signerLocationAbsenceReason', input.signerLocationAbsenceReason);
  if (input.acceptedClinicAmount !== null) form.append('acceptedClinicAmount', String(input.acceptedClinicAmount));
  // B14 — recomendações e próxima manutenção viajam no multipart (TechServiceOrderEndpoints lê
  // form["recommendations"]/form["nextMaintenance"]); só anexa quando a view os enviou.
  if (input.recommendations !== null && input.recommendations !== undefined) form.append('recommendations', input.recommendations);
  if (input.nextMaintenance !== null && input.nextMaintenance !== undefined) form.append('nextMaintenance', input.nextMaintenance);
  return parseServiceOrder(await httpClient.post<unknown>(`${basePath(requestId)}/close`, form));
}

function dataUrlToBlob(dataUrl: string): Blob {
  const [header, base64] = dataUrl.split(',');
  const mime = header?.match(/data:(.*?);/)?.[1] ?? 'image/png';
  const binary = atob(base64 ?? '');
  const bytes = new Uint8Array(binary.length);
  for (let index = 0; index < binary.length; index += 1) {
    bytes[index] = binary.charCodeAt(index);
  }
  return new Blob([bytes], { type: mime });
}

/**
 * GET …/service-order/pdf — baixa o PDF da OS fechada (F73). Fetch autenticado direto porque o
 * corpo é binário (application/pdf), fora do httpClient JSON-only; mesma política de token/CSRF.
 */
export async function downloadServiceOrderPdf(requestId: string): Promise<void> {
  const env = loadEnv();
  const token = getSession().accessToken;
  const response = await fetch(`${env.VITE_API_BASE_URL}${basePath(requestId)}/pdf`, {
    method: 'GET',
    credentials: 'include',
    headers: {
      Accept: 'application/pdf',
      ...(token !== null ? { Authorization: `Bearer ${token}` } : {}),

    },
  }).catch(() => undefined);
  if (response === undefined) {
    throw new ApiError({ status: 0, correlationId: undefined, kind: 'network', message: 'Não foi possível baixar a ordem de serviço.' });
  }
  if (!response.ok) {
    throw new ApiError({ status: response.status, correlationId: undefined, kind: 'http', message: 'Não foi possível baixar a ordem de serviço.' });
  }
  const blob = await response.blob();
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = `ordem-servico-${requestId}.pdf`;
  anchor.click();
  URL.revokeObjectURL(url);
}
